import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Combobox } from "@/components/ui/combobox";
import type { SelectOptionItem } from "@/lib/models/ui";

import type { FileEntry } from "../state/types";
import { PdfFileCard } from "./PdfFileCard";

export type UngroupedMoveTarget = {
  group_id: string;
  /** Customer long_name, or a placeholder when no customer is selected. */
  label: string;
};

type UngroupedPanelProps = {
  ungrouped_file_ids: string[];
  files: Record<string, FileEntry>;
  /**
   * Groups that may receive files. Approved groups are not editable in
   * Phase 3 and must not appear here.
   */
  movable_target_groups: UngroupedMoveTarget[];

  selected_file_ids: string[];
  onToggleSelect: (fileId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;

  onCreateManualGroup: (fileIds: string[]) => void;
  onMoveToGroup: (fileIds: string[], targetGroupId: string) => void;
};

/**
 * Panel for bucket-3 / ungrouped files in Phase 3.
 *
 * - Each file is rendered as a `PdfFileCard` with a left-side checkbox
 *   and a right-side single-file "Move to…" picker.
 * - Bulk actions ("Create new group from selection" / "Move selection
 *   to…") operate on the currently selected ids.
 * - Selection is owned by the parent phase so it can be invalidated
 *   when files leave the panel by any path.
 * - Approved groups are intentionally excluded from
 *   `movable_target_groups` so they never appear as a move target.
 */
export function UngroupedPanel({
  ungrouped_file_ids,
  files,
  movable_target_groups,
  selected_file_ids,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onCreateManualGroup,
  onMoveToGroup,
}: UngroupedPanelProps) {
  // Local UI scratch state for the bulk-move picker. Reset to "" after
  // every successful move so the picker doesn't keep a stale value.
  const [bulkMoveTargetId, setBulkMoveTargetId] = useState<string>("");

  const selectedSet = useMemo(
    (): Set<string> => new Set<string>(selected_file_ids),
    [selected_file_ids],
  );

  const moveOptions = useMemo(
    (): SelectOptionItem<string>[] =>
      movable_target_groups.map((t: UngroupedMoveTarget) => ({
        value: t.group_id,
        label: t.label,
      })),
    [movable_target_groups],
  );

  const noFiles = ungrouped_file_ids.length === 0;
  const allSelected =
    !noFiles && selectedSet.size === ungrouped_file_ids.length;
  const anySelected = selectedSet.size > 0;
  const noTargets = movable_target_groups.length === 0;

  const handleCreateGroup = () => {
    if (selected_file_ids.length === 0) return;
    onCreateManualGroup(selected_file_ids);
  };

  const handleBulkMove = () => {
    if (selected_file_ids.length === 0) return;
    if (bulkMoveTargetId === "") return;
    onMoveToGroup(selected_file_ids, bulkMoveTargetId);
    setBulkMoveTargetId("");
  };

  return (
    <div className="rounded-md border-2 border-dashed border-muted-foreground/30 bg-muted/10 p-3 flex flex-col gap-3">
      <div className="flex flex-row items-start justify-between gap-2 flex-wrap">
        <div className="flex flex-col gap-1 min-w-0">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Ungrouped files
          </h3>
          <p className="text-xs text-muted-foreground">
            {noFiles
              ? "All files are grouped."
              : `${ungrouped_file_ids.length} file${
                  ungrouped_file_ids.length === 1 ? "" : "s"
                } unassigned. ${selected_file_ids.length} selected.`}
          </p>
        </div>
        {!noFiles ? (
          <div className="flex flex-row gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={allSelected ? onClearSelection : onSelectAll}
            >
              {allSelected ? "Clear selection" : "Select all"}
            </Button>
          </div>
        ) : null}
      </div>

      {!noFiles ? (
        <div className="flex flex-row items-center gap-2 flex-wrap rounded-md border bg-card p-2">
          <Button
            size="sm"
            onClick={handleCreateGroup}
            disabled={!anySelected}
          >
            Create new group from selection
          </Button>
          <div className="flex flex-row items-center gap-1">
            <span className="text-xs text-muted-foreground">
              Move selection to:
            </span>
            <Combobox
              options={moveOptions}
              value={bulkMoveTargetId}
              onValueChange={(v: string) => setBulkMoveTargetId(v)}
              placeholder={
                noTargets ? "No groups available" : "Pick a group..."
              }
              disabled={!anySelected || noTargets}
              virtualizeList
              dwimOnChange
              isModal
              popoverClassName="md:min-w-[300px] lg:min-w-[400px]"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkMove}
              disabled={
                !anySelected || noTargets || bulkMoveTargetId === ""
              }
            >
              Move
            </Button>
          </div>
        </div>
      ) : null}

      {!noFiles ? (
        <ul className="flex flex-col gap-1 max-h-96 overflow-y-auto">
          {ungrouped_file_ids.map((fileId: string) => {
            const entry = files[fileId];
            if (!entry) return null;
            const isSelected = selectedSet.has(fileId);
            return (
              <li key={fileId}>
                <PdfFileCard
                  filename={entry.file.name}
                  density="compact"
                  leadingBadge={
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelect(fileId)}
                      aria-label={`Select ${entry.file.name}`}
                    />
                  }
                  actions={
                    <SingleFileMovePicker
                      options={moveOptions}
                      disabled={noTargets}
                      onMove={(targetGroupId: string) =>
                        onMoveToGroup([fileId], targetGroupId)
                      }
                    />
                  }
                />
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

type SingleFileMovePickerProps = {
  options: SelectOptionItem<string>[];
  disabled?: boolean;
  onMove: (targetGroupId: string) => void;
};

/**
 * Tiny per-row "Move to group" picker. Calls `onMove` immediately on
 * value change, then resets so the same row can move again to a
 * different group.
 */
function SingleFileMovePicker({
  options,
  disabled,
  onMove,
}: SingleFileMovePickerProps) {
  const [value, setValue] = useState<string>("");
  return (
    <div className="flex flex-row items-center gap-1">
      <span className="text-[10px] uppercase text-muted-foreground">
        Move →
      </span>
      <Combobox
        options={options}
        value={value}
        onValueChange={(v: string) => {
          if (v === "") return;
          onMove(v);
          setValue("");
        }}
        placeholder={disabled ? "No groups" : "Pick a group..."}
        disabled={disabled}
        virtualizeList
        dwimOnChange
        isModal
        popoverClassName="md:min-w-[300px] lg:min-w-[400px]"
      />
    </div>
  );
}
